import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { FormattedMessage, useIntl } from 'react-intl';
import InstallPromptLocation from '@alltrails/amplitude/enums/InstallPromptLocation';
import { useEffect, useState } from 'react';
import logInstallPromptViewed from '@alltrails/amplitude/events/logInstallPromptViewed';
import logInstallPromptDismissed from '@alltrails/amplitude/events/logInstallPromptDismissed';
import logContinueInBrowserClicked from '@alltrails/amplitude/events/logContinueInBrowserClicked';
import Image from '@alltrails/core/components/Image';
import Typography from '@alltrails/core/components/Typography';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import { Modal } from '@alltrails/modal';
import AppIcon from '@alltrails/denali/components/AppIcon';
import { useDispatch, useSelector } from 'react-redux';
import { useExperiments, useExposureEvent } from '@alltrails/experiments';
import type { userAgent } from 'next/server';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import useDeeplink from '../../hooks/useDeeplink';
import { deepLinkRedirect } from '../../utils';
import getBrowserData from '../../utils/getBrowserData';
import { setIsOpen } from '../../slices/installBanner';
import styles from './styles/InstallBanner.module.scss';

type InstallBannerProps = {
  deeplinkData?: DeeplinkData;
  promptLocation: InstallPromptLocation;
  isOpen?: boolean;
  onRequestClose?: () => void;
};

// Design link: https://www.figma.com/file/8Vz0I2E3m5cBiRc0JmLSHJ/Growth-Optimizations-%E2%80%A2-H1?type=design&node-id=149-7294&mode=design&t=3x358HG71vqj9opp-0

const InstallBanner = ({ deeplinkData, promptLocation, isOpen: isOpenAttempt = false, onRequestClose }: InstallBannerProps) => {
  const browserName = useSelector((state: { browser?: ReturnType<typeof userAgent>['browser'] }) => state.browser?.name);
  const isMobile = useIsMobileSizedScreen();
  const intl = useIntl();
  const isOpen = isOpenAttempt && isMobile;
  const getDeeplink = useDeeplink({ deeplinkData, isOpen, promptType: 'banner' });

  const experiments = useExperiments();
  const isAppsFlyerEnabled = experiments['web-appsflyer-migration']?.value === 'on';
  const ariaLabelElementId = 'install-prompt-label';
  const dispatch = useDispatch();
  const sendExposure = useExposureEvent('web-appsflyer-migration');

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    if (isOpen && isMobile) {
      sendExposure();
    }
  }, [isOpen, isMobile, sendExposure]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isMounted) {
      dispatch(setIsOpen(isOpen));
    }
  }, [isOpen, isMounted]);

  useEffect(() => {
    if (isOpen) {
      logInstallPromptViewed({
        install_prompt_location: promptLocation,
        install_prompt_type: 'banner',
        install_prompt_source: isAppsFlyerEnabled ? 'native_with_appsflyer_link' : 'native'
      });
    }
  }, [isAppsFlyerEnabled, isOpen, promptLocation]);

  const handleCloseClick = () => {
    logInstallPromptDismissed({
      install_prompt_location: promptLocation,
      install_prompt_type: 'banner',
      install_prompt_source: isAppsFlyerEnabled ? 'native_with_appsflyer_link' : 'native'
    });
    onRequestClose?.();
  };

  const { src, buttonText } = getBrowserData(intl, browserName);

  return (
    <Modal
      ariaLabelElementId={ariaLabelElementId}
      backgroundClassName={styles.background}
      focusTrapOptions={{ allowOutsideClick: true, clickOutsideDeactivates: false }}
      isOpen={isOpen}
      onCloseRequest={() => {}}
      modalClassName={styles.modal}
      shouldHideHeader
      initialFocus={false}
    >
      <div className={styles.variantContainer}>
        <button
          data-testid="continue-in-app"
          className={styles.optionContainer}
          onClick={e => {
            e.stopPropagation();
            deepLinkRedirect(getDeeplink(), 'banner', promptLocation, isAppsFlyerEnabled ? 'native_with_appsflyer_link' : 'native');
          }}
        >
          <div className={styles.optionInfo}>
            <AppIcon size={60} />
            <Typography variant="heading200" id={ariaLabelElementId}>
              <FormattedMessage defaultMessage="Continue in app" />
            </Typography>
          </div>
          <ChevronSm orientation="right" />
        </button>
        <div className={styles.divider} />
        <button
          data-testid="continue-in-browser"
          className={styles.optionContainer}
          onClick={e => {
            e.stopPropagation();
            logContinueInBrowserClicked({
              install_prompt_location: promptLocation,
              install_prompt_type: 'banner',
              install_prompt_source: isAppsFlyerEnabled ? 'native_with_appsflyer_link' : 'native'
            });
            handleCloseClick();
          }}
        >
          <div className={styles.optionInfo}>
            <div className={styles.browserImageContainer}>{isMounted && <Image src={src} alt="" width={40} height={40} />}</div>
            <Typography variant="heading200">{buttonText}</Typography>
          </div>
          <ChevronSm orientation="right" />
        </button>
      </div>
    </Modal>
  );
};

export default InstallBanner;
